import React from 'react';
import { Box, Container, Divider, Stack, Typography } from '@mui/material';

const Section = ({ title, children }) => (
  <Stack spacing={1.5}>
    <Typography variant="h6" fontWeight={700}>{title}</Typography>
    {children}
  </Stack>
);

const Body = ({ children }) => (
  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
    {children}
  </Typography>
);

const PrivacyPolicyPage = () => (
  <Box sx={{ minHeight: '100vh', py: { xs: 5, md: 8 }, bgcolor: 'background.default' }}>
    <Container maxWidth="md">
      <Stack spacing={5}>
        <Stack spacing={1}>
          <Typography variant="h4" fontWeight={700}>Privacy Policy</Typography>
          <Typography variant="body2" color="text.secondary">
            Mister Food Safety (OPC) Private Limited &mdash; Effective date: 13 May 2026
          </Typography>
        </Stack>

        <Divider />

        <Section title="1. Overview">
          <Body>
            This Privacy Policy describes how Mister Food Safety (OPC) Private Limited
            (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) collects, uses, and protects information when you use
            the Mister Food Safety mobile application and web portal (the &quot;App&quot;). The App is
            a private, invite-only platform available exclusively to existing customers of
            Mister Food Safety. It is not open to the general public.
          </Body>
        </Section>

        <Section title="2. Information We Collect">
          <Body>
            We collect only the minimum information necessary to provide the service:
          </Body>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {[
              'Email address — used to authenticate you and identify your account.',
              'User ID — a unique identifier generated by our authentication provider (Google Firebase) upon account creation.',
              'Full name — used to identify personnel associated with your business account.',
              'Phone number — used for contact and communication purposes within the platform.',
              'Business address — used to associate your account with the correct branch or location.',
            ].map((item) => (
              <Box component="li" key={item} sx={{ mb: 1 }}>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <Body>
            We do not collect payment information, precise location data, or any other personal
            data beyond the above.
          </Body>
        </Section>

        <Section title="3. How We Use Your Information">
          <Body>We use the information collected solely to:</Body>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {[
              'Verify your identity and grant access to the App.',
              'Maintain your authenticated session so you do not need to log in on every visit.',
              'Associate your account with the correct business and documents assigned to you by Mister Food Safety.',
            ].map((item) => (
              <Box component="li" key={item} sx={{ mb: 1 }}>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <Body>
            We do not use your information for advertising, profiling, or any purpose unrelated
            to delivering the App to you.
          </Body>
        </Section>

        <Section title="4. Data Storage and Security">
          <Body>
            Your personal information and authentication data are processed and stored on secure,
            industry-standard cloud infrastructure. All data transmitted between the App and our
            servers is encrypted in transit. Data at rest is protected using encryption and access
            controls that meet industry best practices.
          </Body>
          <Body>
            Your compliance documents and business records are stored on secure cloud servers
            located in India. Access to these documents is strictly restricted to authorised users
            of your business account only. We regularly review our security practices to ensure
            your data remains protected.
          </Body>
        </Section>

        <Section title="5. Sharing of Information">
          <Body>
            We do not sell, rent, or share your personal information with any third party for
            commercial purposes. Your data is shared only with the following service providers
            strictly to operate the App:
          </Body>
          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            {[
              'Cloud authentication providers — for secure login and session management.',
              'Cloud infrastructure providers — for document storage and backend services.',
            ].map((item) => (
              <Box component="li" key={item} sx={{ mb: 1 }}>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <Body>
            All third-party providers are contractually bound to protect your data and are not
            permitted to use it for any purpose other than providing services to us.
          </Body>
        </Section>

        <Section title="6. Data Retention">
          <Body>
            We retain your account data for as long as you are an active customer of Mister Food
            Safety. If your account is deactivated or your engagement with us ends, your data will
            be deleted from our systems within 90 days of account closure upon written request to
            admin@misterfoodsafety.com.
          </Body>
        </Section>

        <Section title="7. Your Rights">
          <Body>
            You have the right to request access to, correction of, or deletion of the personal
            data we hold about you. To exercise any of these rights, please contact us at the
            address below. We will respond within 30 days.
          </Body>
        </Section>

        <Section title="8. Children's Privacy">
          <Body>
            The App is intended for use by business professionals aged 18 and above. We do not
            knowingly collect information from anyone under the age of 18. If you believe a minor
            has provided us with personal information, please contact us immediately.
          </Body>
        </Section>

        <Section title="9. Changes to This Policy">
          <Body>
            We may update this Privacy Policy from time to time. When we do, we will update the
            effective date at the top of this page. Continued use of the App after any changes
            constitutes your acceptance of the revised policy. We encourage you to review this
            page periodically.
          </Body>
        </Section>

        <Section title="10. Contact Us">
          <Body>
            If you have any questions or concerns about this Privacy Policy, please contact us:
          </Body>
          <Box sx={{ pl: 1 }}>
            <Typography variant="body1" fontWeight={600}>Mister Food Safety (OPC) Private Limited</Typography>
            <Typography variant="body2" color="text.secondary">
              NO.65/111, ST. JOSEPH'S PRIVATE I.T.I,<br />
              Big Bazaar St, Town Hall,<br />
              Coimbatore, Tamil Nadu 641001, India
            </Typography>
            <Typography
              component="a"
              href="mailto:admin@misterfoodsafety.com"
              variant="body2"
              sx={{ color: 'primary.main', textDecoration: 'none', display: 'block', mt: 0.5 }}
            >
              admin@misterfoodsafety.com
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              +91 77080 54431 &nbsp;|&nbsp; +91 70924 92494
            </Typography>
          </Box>
        </Section>

        <Divider />

        <Typography variant="caption" color="text.disabled" textAlign="center">
          &copy; {new Date().getFullYear()} Mister Food Safety (OPC) Private Limited. All rights reserved.
        </Typography>
      </Stack>
    </Container>
  </Box>
);

export default PrivacyPolicyPage;
